import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useOutletContext } from 'react-router-dom';
import { MessageSquare, Lock, ExternalLink, ChevronDown, Loader2, Sparkles, Copy, Check, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { getClients, getSessions } from '../../lib/coaching-sessions';
import type { CoachingClient, CoachingSession } from '../../lib/coaching-sessions';

interface OutletCtx { isUnlocked: boolean; }
const WALKTHROUGH_URL = 'https://sessionintelligence.app/walkthrough';

const TONE_OPTIONS = ['Warm', 'Concise', 'Reflective', 'Professional', 'Direct'];

function buildFollowUpPrompt(clientName: string, tone: string, session: CoachingSession): string {
  const a = session.session_analysis!;
  const lines: string[] = [];

  lines.push(`Generate a ${tone.toLowerCase()} follow-up message from the coach to their client ${clientName}, based on the session analysis below.`);
  lines.push('');
  lines.push('The message should:');
  lines.push('- Open with a brief, warm acknowledgement of the session');
  lines.push('- Highlight 1-2 key themes that emerged from the conversation');
  lines.push('- Include a single, thoughtful reflection question for the client to sit with');
  lines.push('- Optionally suggest a light between-session task or homework if appropriate');
  lines.push('- Close warmly and professionally');
  lines.push('- Be addressed to the client by first name');
  lines.push('- Be suitable to send via email or message');
  lines.push('');
  lines.push(`Tone: ${tone}`);
  lines.push('');
  lines.push('Session Analysis:');
  lines.push(`Overview: ${a.session_summary.overview}`);
  lines.push(`Main topic: ${a.session_summary.main_topic}`);
  if (a.breakthrough_zone) {
    lines.push(`Breakthrough zone: ${a.breakthrough_zone.area_of_deepest_leverage}`);
  }
  if (a.limiting_beliefs?.length) {
    lines.push(`Key client language: ${a.limiting_beliefs.slice(0, 2).map(b => `"${b.client_quote}"`).join(', ')}`);
  }
  if (a.next_session_prep?.questions_to_ask?.length) {
    lines.push(`Suggested reflection question (choose one or adapt): ${a.next_session_prep.questions_to_ask[0]}`);
  }
  if (a.session_summary.agreed_actions?.length) {
    lines.push(`Agreed actions: ${a.session_summary.agreed_actions.join(', ')}`);
  }

  return lines.join('\n');
}

export function SIFollowUpAssistantPage() {
  const { isUnlocked } = useOutletContext<OutletCtx>();

  const [clients, setClients] = useState<CoachingClient[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [sessions, setSessions] = useState<CoachingSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [selectedTone, setSelectedTone] = useState('Warm');
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [coachId, setCoachId] = useState('');

  useEffect(() => {
    if (!isUnlocked) return;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: coach } = await supabase.from('coaches').select('id').eq('user_id', user.id).maybeSingle();
      if (!coach) return;
      setCoachId(coach.id);
      const clientList = await getClients(coach.id);
      setClients(clientList);
    })();
  }, [isUnlocked]);

  useEffect(() => {
    if (!selectedClientId || !coachId) return;
    setMessage('');
    setSelectedSessionId('');
    (async () => {
      const all = await getSessions(coachId, selectedClientId);
      setSessions(all.filter(s => s.analysis_status === 'complete' && s.session_analysis));
    })();
  }, [selectedClientId, coachId]);

  const selectedClient = clients.find(c => c.id === selectedClientId);
  const selectedSession = sessions.find(s => s.id === selectedSessionId);

  const handleGenerate = async () => {
    if (!selectedSession?.session_analysis || !selectedClient) return;
    setGenerating(true);
    setError('');
    setMessage('');

    try {
      const prompt = buildFollowUpPrompt(selectedClient.full_name, selectedTone, selectedSession);

      const { data: fnData, error: fnError } = await supabase.functions.invoke('coach-intelligence-ai', {
        body: {
          messages: [{ role: 'coach', content: prompt }],
          isDemo: false,
          clientName: selectedClient.full_name,
          sessionSummary: selectedSession.session_analysis!.session_summary.overview,
          contextType: 'follow-up-message',
        },
      });

      if (fnError) throw new Error(fnError.message);
      if (fnData?.error) throw new Error(fnData.error);

      setMessage(fnData.reply || '');
    } catch (e) {
      setError((e as Error).message || 'Failed to generate message.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(message);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-2xl font-bold text-gray-80">Follow-Up Assistant</h1>
        <p className="text-sm text-gray-50 mt-1">
          Write thoughtful follow-up messages after a session — helping clients stay connected to the work between conversations.
        </p>
      </motion.div>

      {!isUnlocked && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-2xl border border-gray-20 p-6 flex items-start gap-4"
        >
          <div className="p-3 bg-gray-10 rounded-xl flex-shrink-0">
            <Lock className="w-5 h-5 text-gray-40" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-80 mb-1">Follow-Up Assistant</h2>
            <p className="text-sm text-gray-50 mb-4 leading-relaxed">
              After your first session is analysed, Session Intelligence™ will help you write warm, targeted follow-up messages tailored to each client's themes and language patterns.
            </p>
            <a
              href={WALKTHROUGH_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-success-40 hover:bg-success-50 text-white rounded-xl text-sm font-semibold transition-colors"
            >
              Unlock Follow-Up Assistant on the Walkthrough
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </motion.div>
      )}

      {isUnlocked && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-2xl border border-gray-20 p-5 space-y-4"
        >
          <h2 className="text-sm font-semibold text-gray-80">Generate a Follow-Up Message</h2>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-50 mb-1.5">Select Client</label>
              <div className="relative">
                <select
                  value={selectedClientId}
                  onChange={e => setSelectedClientId(e.target.value)}
                  className="w-full appearance-none bg-gray-5 border border-gray-20 rounded-xl px-3 py-2.5 text-sm text-gray-70 pr-8 focus:outline-none focus:ring-2 focus:ring-success-30"
                >
                  <option value="">-- Choose a client --</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.full_name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-40 pointer-events-none" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-50 mb-1.5">Select Session</label>
              <div className="relative">
                <select
                  value={selectedSessionId}
                  onChange={e => setSelectedSessionId(e.target.value)}
                  disabled={!selectedClientId}
                  className="w-full appearance-none bg-gray-5 border border-gray-20 rounded-xl px-3 py-2.5 text-sm text-gray-70 pr-8 focus:outline-none focus:ring-2 focus:ring-success-30 disabled:opacity-50"
                >
                  <option value="">-- Choose a session --</option>
                  {sessions.map(s => (
                    <option key={s.id} value={s.id}>
                      {new Date(s.session_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {s.notes ? ` — ${s.notes.slice(0, 40)}` : ''}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-40 pointer-events-none" />
              </div>
              {selectedClientId && sessions.length === 0 && (
                <p className="text-xs text-gray-40 mt-1">No analysed sessions yet for this client.</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-50 mb-1.5">Message Tone</label>
            <div className="flex flex-wrap gap-2">
              {TONE_OPTIONS.map(t => (
                <button
                  key={t}
                  onClick={() => setSelectedTone(t)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${
                    selectedTone === t
                      ? 'bg-success-40 text-white'
                      : 'bg-gray-10 text-gray-60 hover:bg-gray-20'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-700">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <button
            onClick={handleGenerate}
            disabled={!selectedSessionId || generating}
            className="flex items-center gap-2 px-5 py-2.5 bg-success-40 hover:bg-success-50 disabled:opacity-50 text-white rounded-xl text-sm font-semibold transition-colors"
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Generating Message…</>
            ) : (
              <><Sparkles className="w-4 h-4" /> Generate Follow-Up</>
            )}
          </button>
        </motion.div>
      )}

      {isUnlocked && message && (
        <motion.div
          key="generated-message"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-gray-20 overflow-hidden"
        >
          <div className="px-5 py-4 border-b border-gray-10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-success-40 flex items-center justify-center text-white text-xs font-bold">
                {selectedClient?.full_name.slice(0, 2).toUpperCase()}
              </div>
              <div>
                <p className="text-xs text-gray-40 font-medium">{selectedClient?.full_name} — {selectedTone} tone</p>
                <h3 className="text-sm font-bold text-gray-80">Generated Follow-Up Message</h3>
              </div>
            </div>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-10 hover:bg-gray-20 text-gray-60 rounded-xl text-xs font-medium transition-colors"
            >
              {copied ? <><Check className="w-3.5 h-3.5 text-success-50" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
            </button>
          </div>
          <div className="p-5">
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              className="w-full min-h-[280px] bg-brand-5 border border-gray-20 rounded-2xl p-5 font-body text-sm text-gray-70 leading-relaxed focus:outline-none focus:ring-2 focus:ring-success-30 resize-y"
            />
            <p className="text-xs text-gray-40 mt-2">Edit before sending. This message is generated from session analysis data.</p>
          </div>
        </motion.div>
      )}

      {/* Demo follow-up — always visible when locked */}
      {!isUnlocked && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl border border-gray-20 overflow-hidden"
        >
          <div className="px-5 py-4 border-b border-gray-10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-success-40 flex items-center justify-center text-white text-xs font-bold">SA</div>
              <div>
                <p className="text-xs text-gray-40 font-medium">Sample Client</p>
                <h3 className="text-sm font-bold text-gray-80">Sarah — Sample Follow-Up Message</h3>
              </div>
            </div>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-10 text-orange-70 text-xs font-semibold">Demo Preview</span>
          </div>
          <div className="p-5">
            <div className="bg-brand-5 border border-gray-20 rounded-2xl p-5 font-body text-sm text-gray-60 leading-relaxed space-y-3">
              <p>Hi Sarah,</p>
              <p>
                Great session today. A few themes stood out around boundaries, guilt, and the tension between keeping the peace and being honest about your own needs.
              </p>
              <p>Before our next session, reflect on this question:</p>
              <div className="bg-success-5 border-l-4 border-success-40 pl-4 py-2 rounded-r-xl">
                <p className="font-semibold text-gray-80 italic">
                  "What would saying no protect that saying yes keeps costing me?"
                </p>
              </div>
              <p>
                Bring any thoughts, emotions, or resistance that comes up, and we'll explore it together.
              </p>
              <p>Looking forward to our next conversation.</p>
            </div>
            <p className="text-xs text-gray-40 italic mt-3">This is a sample follow-up message. Your own messages will be generated from real session analyses after unlock.</p>
          </div>
        </motion.div>
      )}

      {/* What it generates */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-white rounded-2xl border border-gray-20 p-5"
      >
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-4 h-4 text-success-50" />
          <h3 className="text-sm font-semibold text-gray-80">What the Follow-Up Assistant Generates</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <ul className="space-y-2">
            {[
              'Warm session summary email or message',
              'Reflection prompt tailored to the session',
              'Homework or action step',
              'Reminder of agreed actions',
            ].map(item => (
              <li key={item} className="flex items-center gap-2 text-sm text-gray-60">
                <div className="w-1.5 h-1.5 rounded-full bg-success-40 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
          <ul className="space-y-2">
            {[
              'Between-session check-in prompt',
              'Tone customisation options',
            ].map(item => (
              <li key={item} className="flex items-center gap-2 text-sm text-gray-60">
                <div className="w-1.5 h-1.5 rounded-full bg-success-40 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-4">
          <p className="text-xs font-semibold text-gray-50 uppercase tracking-wide mb-2">Tone Options</p>
          <div className="flex flex-wrap gap-2">
            {TONE_OPTIONS.map(t => (
              <span key={t} className="px-3 py-1 bg-gray-10 text-gray-60 rounded-full text-xs font-medium">{t}</span>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
